import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import './ToolPage.css'
import './PasswordGenerator.css'
import {
  generatePassword,
  passwordStrength,
  poolSizeFor,
} from './utils/passwordGenerator'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const DEFAULT_OPTIONS = {
  length: 16,
  lower: true,
  upper: true,
  numbers: true,
  symbols: true,
  excludeAmbiguous: false,
}

const PasswordGenerator = () => {
  useDocumentMeta({
    title: 'Gerador de Senha Forte Online e Grátis  jarder.dev',
    description:
      'Gere senhas fortes e aleatórias, com letras, números e símbolos. ' +
      'Rápido, seguro (gerado no seu navegador) e sem cadastro.',
  })

  const [options, setOptions] = useState(DEFAULT_OPTIONS)
  const [password, setPassword] = useState(() =>
    generatePassword(DEFAULT_OPTIONS),
  )
  const [copied, setCopied] = useState(false)

  const generate = (nextOptions = options) => {
    const password = generatePassword(nextOptions)
    setPassword(password)
    trackEvent('tool_generate', { tool: 'password' })
  }

  const handleOptionChange = (key) => {
    const nextOptions = { ...options, [key]: !options[key] }
    setOptions(nextOptions)
    generate(nextOptions)
  }

  const handleLengthChange = (value) => {
    const nextOptions = { ...options, length: value }
    setOptions(nextOptions)
    generate(nextOptions)
  }

  const copy = () => {
    navigator.clipboard.writeText(password).then(() => {
      setCopied(true)
      trackEvent('tool_copy', { tool: 'password' })
      setTimeout(() => setCopied(false), 1500)
    })
  }

  const strength = passwordStrength(password, poolSizeFor(options))

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Gerador de Senha Forte</h1>
      <p className="tool-disclaimer">
        Gerada com <code>crypto.getRandomValues</code> direto no seu
        navegador  nada é enviado para nenhum servidor.
      </p>

      <div className="tool-result-card">
        <span className="tool-result-label">Senha gerada</span>
        <span className="tool-result-value password-value">{password}</span>
        <div className="tool-result-actions">
          <button
            type="button"
            className="tool-button tool-button-secondary"
            onClick={copy}
          >
            Copiar
          </button>
          <button type="button" className="tool-button" onClick={() => generate()}>
            Gerar nova
          </button>
        </div>
        {copied && <span className="tool-copied">Copiado!</span>}
        <div className="password-strength">
          <div className={`password-strength-bar level-${strength.level}`} />
          <span>{strength.label}</span>
        </div>
      </div>

      <div className="tool-field">
        <label htmlFor="length">Comprimento: {options.length}</label>
        <input
          id="length"
          type="range"
          min="6"
          max="48"
          value={options.length}
          onChange={(e) => handleLengthChange(Number(e.target.value))}
        />
      </div>

      <div className="password-options">
        <label>
          <input
            type="checkbox"
            checked={options.lower}
            onChange={() => handleOptionChange('lower')}
          />
          Letras minúsculas (a-z)
        </label>
        <label>
          <input
            type="checkbox"
            checked={options.upper}
            onChange={() => handleOptionChange('upper')}
          />
          Letras maiúsculas (A-Z)
        </label>
        <label>
          <input
            type="checkbox"
            checked={options.numbers}
            onChange={() => handleOptionChange('numbers')}
          />
          Números (0-9)
        </label>
        <label>
          <input
            type="checkbox"
            checked={options.symbols}
            onChange={() => handleOptionChange('symbols')}
          />
          Símbolos (!@#$...)
        </label>
        <label>
          <input
            type="checkbox"
            checked={options.excludeAmbiguous}
            onChange={() => handleOptionChange('excludeAmbiguous')}
          />
          Excluir caracteres ambíguos (I, l, 1, O, 0)
        </label>
      </div>

      <div className="tool-info-section">
        <h2>O que torna uma senha forte</h2>
        <p>
          O principal fator é o comprimento: cada caractere a mais
          multiplica o número de combinações possíveis, tornando um
          ataque de força bruta exponencialmente mais demorado. Misturar
          maiúsculas, minúsculas, números e símbolos aumenta ainda mais
          essa variedade. Evite reutilizar a mesma senha em vários
          sites  se um deles vazar, as outras contas ficam expostas
          também. Um gerenciador de senhas ajuda a guardar senhas longas
          e únicas sem precisar decorá-las.
        </p>
      </div>
    </div>
  )
}

export default PasswordGenerator
